import { DateValue } from 'components/filter/input/DateRangePicker/types';
import { FormProvider, useForm } from 'react-hook-form';
import { ReactNode, useEffect, useRef, useMemo, useCallback } from 'react';
import { CountryCodeType } from 'models/countries';
import { useAppStore } from 'hooks/state/useAppStore';
import type { AdvancedFilterValues } from 'hooks/state/FilterSlice';
import { useFilterSetsSessionStore } from 'hooks/state/usePersistedStore';
import { getDefaultFilterValues } from 'utils/filterDefaults';
import { FilterFormMethodsContext, FilterFormMethods } from './hooks/useFilterFormMethods';

export interface AdvancedFilterData {
  selectedDateRange: DateValue;
  selectedCreationDateRange: DateValue;
  selectedBrands: string[];
  selectedMarketplaces: string[];
  selectedFulfillmentMethods: string[];
  selectedCategories: string[];
  selectedCountries: CountryCodeType[];
  selectedProducts: string[];
  selectedGenders: string[];
  selectedAgeGroups: string[];
  selectedSeasons: string[];
  selectedColors: string[];
  selectedSizes: string[];
  selectedBenchmark: string;
  formSelectedFilterSet?: string | null;
  formLastClickedFilterSet?: string | null;
}

export interface AdvancedFilterFormControllerProps {
  onClose: () => void;
}

export function FilterFormController({
  onClose,
  children,
  className = '',
}: AdvancedFilterFormControllerProps & {
  children: ReactNode;
  className?: string;
}) {
  const {
    brands,
    dateRange,
    creationDateRange,
    countries,
    categories,
    genders,
    ageGroups,
    seasons,
    marketplaces,
    fulfillmentMethods,
    products,
    colors,
    sizes,
    benchmark,
    setAdvancedFilters,
  } = useAppStore();

  // Get filter set selection state from session store
  const { selectedFilterSet, lastClickedFilterSet, setSelectedFilterSet, setLastClickedFilterSet } =
    useFilterSetsSessionStore();

  const methods = useForm<AdvancedFilterData>({
    defaultValues: {
      selectedDateRange: dateRange,
      selectedCreationDateRange: creationDateRange,
      selectedBrands: brands ?? [],
      selectedMarketplaces: marketplaces ?? [],
      selectedFulfillmentMethods: fulfillmentMethods ?? [],
      selectedCategories: categories ?? [],
      selectedGenders: genders ?? [],
      selectedAgeGroups: ageGroups ?? [],
      selectedSeasons: seasons ?? [],
      selectedCountries: countries ?? [],
      selectedProducts: products ?? [],
      selectedColors: colors ?? [],
      selectedSizes: sizes ?? [],
      selectedBenchmark: benchmark,
      formSelectedFilterSet: selectedFilterSet,
      formLastClickedFilterSet: lastClickedFilterSet,
    },
  });

  const { handleSubmit, reset } = methods;

  // Prevents race condition between manual reset and automatic app store sync
  const skipNextReset = useRef(false);

  // Auto-sync form with app store values when they change
  useEffect(() => {
    if (skipNextReset.current) {
      skipNextReset.current = false;
      return;
    }

    reset({
      selectedDateRange: dateRange,
      selectedCreationDateRange: creationDateRange,
      selectedBrands: brands ?? [],
      selectedMarketplaces: marketplaces ?? [],
      selectedFulfillmentMethods: fulfillmentMethods ?? [],
      selectedCategories: categories ?? [],
      selectedGenders: genders ?? [],
      selectedAgeGroups: ageGroups ?? [],
      selectedSeasons: seasons ?? [],
      selectedCountries: countries ?? [],
      selectedProducts: products ?? [],
      selectedColors: colors ?? [],
      selectedSizes: sizes ?? [],
      selectedBenchmark: benchmark,
      formSelectedFilterSet: selectedFilterSet,
      formLastClickedFilterSet: lastClickedFilterSet,
    });
  }, [
    reset,
    dateRange,
    creationDateRange,
    brands,
    marketplaces,
    fulfillmentMethods,
    categories,
    genders,
    ageGroups,
    seasons,
    countries,
    products,
    colors,
    sizes,
    benchmark,
    selectedFilterSet,
    lastClickedFilterSet,
  ]);

  const resetToAppStore = useCallback(() => {
    reset({
      selectedDateRange: dateRange,
      selectedCreationDateRange: creationDateRange,
      selectedBrands: brands ?? [],
      selectedMarketplaces: marketplaces ?? [],
      selectedFulfillmentMethods: fulfillmentMethods ?? [],
      selectedCategories: categories ?? [],
      selectedGenders: genders ?? [],
      selectedAgeGroups: ageGroups ?? [],
      selectedSeasons: seasons ?? [],
      selectedCountries: countries ?? [],
      selectedProducts: products ?? [],
      selectedColors: colors ?? [],
      selectedSizes: sizes ?? [],
      selectedBenchmark: benchmark,
      formSelectedFilterSet: selectedFilterSet,
      formLastClickedFilterSet: lastClickedFilterSet,
    });
  }, [
    reset,
    dateRange,
    creationDateRange,
    brands,
    marketplaces,
    fulfillmentMethods,
    categories,
    genders,
    ageGroups,
    seasons,
    countries,
    products,
    colors,
    sizes,
    benchmark,
    selectedFilterSet,
    lastClickedFilterSet,
  ]);

  const resetToDefaults = useCallback(() => {
    skipNextReset.current = true; // Prevent useEffect from overriding this reset
    const defaultValues = getDefaultFilterValues();
    reset({
      ...defaultValues,
      formSelectedFilterSet: null,
      formLastClickedFilterSet: null,
    });
  }, [reset]);

  const submit = useCallback(
    (data: AdvancedFilterData) => {
      // The store-sync effect should not reset the form while the dialog is closing.
      skipNextReset.current = true;
      onClose();

      const filters: AdvancedFilterValues = {
        dateRange: data.selectedDateRange,
        creationDateRange: data.selectedCreationDateRange,
        brands: data.selectedBrands,
        marketplaces: data.selectedMarketplaces,
        fulfillmentMethods: data.selectedFulfillmentMethods,
        categories: data.selectedCategories,
        genders: data.selectedGenders,
        ageGroups: data.selectedAgeGroups,
        seasons: data.selectedSeasons,
        countries: data.selectedCountries,
        products: data.selectedProducts ?? [],
        colors: data.selectedColors ?? [],
        sizes: data.selectedSizes ?? [],
        benchmark: data.selectedBenchmark,
      };
      setAdvancedFilters(filters);

      if (data.formSelectedFilterSet !== undefined) {
        setSelectedFilterSet(data.formSelectedFilterSet);
      }
      if (data.formLastClickedFilterSet !== undefined) {
        setLastClickedFilterSet(data.formLastClickedFilterSet);
      }
    },
    [setAdvancedFilters, setSelectedFilterSet, setLastClickedFilterSet, onClose]
  );

  const filterFormMethods: FilterFormMethods = useMemo(
    () => ({
      resetToAppStore,
      resetToDefaults,
      applyFilters: () => {
        void handleSubmit(submit)();
      },
    }),
    [handleSubmit, submit, resetToAppStore, resetToDefaults]
  );

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <FormProvider {...methods}>
      <FilterFormMethodsContext.Provider value={filterFormMethods}>
        <div className={className}>{children}</div>
      </FilterFormMethodsContext.Provider>
    </FormProvider>
  );
}
